// src/components/ComparisonExportModal.js
import React, { useState, useRef } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const ComparisonExportModal = ({ isOpen, onClose, beforePhotos, afterPhotos, patientContext }) => {
    const printRef = useRef();
    
    // 💡 Active target frame (Top Left, Top Right, or Bottom)
    const [activeTarget, setActiveTarget] = useState('left'); 
    
    // 💡 NEW: Independent indices for Before and After in each set
    const [selectedSlots, setSelectedSlots] = useState({
        left: { before: 0, after: 0 },
        right: { before: 2, after: 2 },
        center: { before: 1, after: 1 }
    });

    if (!isOpen) return null;

    const handleDownload = async () => {
        const element = printRef.current;
        const canvas = await html2canvas(element, { scale: 3, useCORS: true });
        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const imgWidth = pdfWidth - 20;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        pdf.addImage(imgData, 'JPEG', 10, 10, imgWidth, imgHeight);
        pdf.save(`Result_${patientContext.branchHN}_${patientContext.visitDate}.pdf`);
        onClose();
    };

    // Helper to render the pair in the print area
    const renderPrintPair = (label, posKey) => {
        const slots = selectedSlots[posKey];
        const bPhoto = beforePhotos[slots.before];
        const aPhoto = afterPhotos[slots.after];

        return (
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#555', marginBottom: '8px', backgroundColor: '#f5f5f5', padding: '4px' }}>
                    {label}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div style={{ aspectRatio: '9/16', backgroundColor: '#eee', borderRadius: '4px', overflow: 'hidden' }}>
                        {bPhoto ? (
                            <img 
                                src={bPhoto.url} 
                                style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: 'cover',
                                    objectPosition: 'center center' // 🎯 Keeps the face centered in the PDF
                                }} 
                            />
                        ) : (
                            <div style={{ paddingTop: '60%', color: '#ccc' }}>No Photo</div>
                        )}
                    </div>
                    <div style={{ aspectRatio: '9/16', backgroundColor: '#eee', borderRadius: '4px', overflow: 'hidden' }}>
                        {aPhoto ? (
                            <img 
                                src={aPhoto.url} 
                                style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: 'cover',
                                    objectPosition: 'center center' // 🎯 Keeps the face centered in the PDF
                                }} 
                            />
                        ) : (
                            <div style={{ paddingTop: '60%', color: '#ccc' }}>No Photo</div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Helper to render a selection strip
    const renderStrip = (type, title, photos, currentIdx) => (
        <div style={{ marginBottom: '10px' }}>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: type === 'before' ? '#2196f3' : '#e91e63', marginBottom: '5px' }}>
                {title} (Slot {currentIdx + 1} selected)
            </div>
            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '5px' }}>
                {photos.map((photo, idx) => (
                    <div 
                        key={idx} 
                        onClick={() => setSelectedSlots({
                            ...selectedSlots, 
                            [activeTarget]: { ...selectedSlots[activeTarget], [type]: idx }
                        })}
                        style={{ 
                            flex: '0 0 60px', 
                            cursor: 'pointer',
                            opacity: photo ? 1 : 0.2
                        }}
                    >
                        <div style={{ 
                            width: '60px', height: '80px', 
                            border: currentIdx === idx ? `3px solid ${type === 'before' ? '#2196f3' : '#e91e63'}` : '1px solid #ddd',
                            borderRadius: '4px', overflow: 'hidden', backgroundColor: '#eee'
                        }}>
                            {photo && (
                                <img 
                                    src={photo.url} 
                                    style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'cover',
                                        objectPosition: 'center center' // 🎯 Keeps the face centered in the PDF
                                    }} 
                                />
                            )}
                        </div>
                        <div style={{ fontSize: '8px', textAlign: 'center', marginTop: '2px' }}>{idx + 1}</div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 20000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '98%', maxWidth: '1150px', maxHeight: '98vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                
                {/* 🛠️ DOUBLE SELECTION SECTION */}
                <div style={{ padding: '15px 25px', borderBottom: '1px solid #eee', backgroundColor: '#f8f9fa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>🛠️ เลือกรูปทำใบสรุป (Independent Pair Selection):</div>
                        <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer' }}>✖️</button>
                    </div>

                    <div style={{ display: 'flex', gap: '40px' }}>
                        {/* A. Choose Frame */}
                        <div style={{ flex: '0 0 250px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ fontSize: '12px', color: '#666' }}>1. เลือกช่องที่จะแก้ไข (Select Frame):</div>
                            {['left', 'right', 'center'].map(pos => (
                                <button 
                                    key={pos}
                                    onClick={() => setActiveTarget(pos)}
                                    style={{ 
                                        padding: '8px', borderRadius: '6px', 
                                        border: activeTarget === pos ? '3px solid #e91e63' : '1px solid #ccc',
                                        backgroundColor: activeTarget === pos ? '#fce4ec' : 'white',
                                        fontWeight: 'bold', cursor: 'pointer', textAlign: 'left', fontSize: '12px'
                                    }}
                                >
                                    {pos === 'left' ? '📍 ซ้าย (Top Left)' : pos === 'right' ? '📍 ขวา (Top Right)' : '📍 กลาง (Bottom)'}
                                </button>
                            ))}
                        </div>

                        {/* B. The Double Strips */}
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>2. เลือกรูปจากสล็อต (Choose Independent Slots):</div>
                            {renderStrip('before', '❄️ BEFORE PHOTOS / รูปก่อน', beforePhotos, selectedSlots[activeTarget].before)}
                            {renderStrip('after', '🌸 AFTER / FOLLOW-UP PHOTOS / รูปหลัง/ติดตามผล', afterPhotos, selectedSlots[activeTarget].after)}
                        </div>
                    </div>
                </div>

                {/* 📄 PRINT PREVIEW */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px', backgroundColor: '#444' }}>
                    <div ref={printRef} style={{ width: '750px', backgroundColor: 'white', margin: '0 auto', padding: '40px' }}>
                        <div style={{ borderBottom: '3px solid #e91e63', paddingBottom: '15px', marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                            <div>
                                <h1 style={{ margin: 0, color: '#0b52bd', fontSize: '28px' }}>V Clinic by Dr. Fang</h1>
                                <div style={{ fontSize: '14px', color: '#666' }}>CLINICAL PROGRESS SUMMARY</div>
                            </div>
                            <div style={{ textAlign: 'right', fontSize: '14px' }}>
                                <strong>Patient:</strong> {patientContext.name}<br/>
                                <strong>HN:</strong> {patientContext.branchHN} | <strong>Date:</strong> {patientContext.visitDate}
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '40px' }}>
                            {renderPrintPair("PROFILE 1", "left")}
                            {renderPrintPair("PROFILE 2", "right")}
                        </div>
                        
                        <div style={{ width: '50%', margin: '0 auto' }}>
                            {renderPrintPair("PROFILE 3", "center")}
                        </div>
                    </div>
                </div>

                <div style={{ padding: '15px', textAlign: 'center', borderTop: '1px solid #eee' }}>
                    <button onClick={handleDownload} style={{ padding: '15px 80px', backgroundColor: '#e91e63', color: 'white', border: 'none', borderRadius: '30px', fontWeight: 'bold', fontSize: '18px', cursor: 'pointer' }}>
                        📥 ดาวน์โหลด PDF รูปก่อน/หลัง/ติดตามผล (Download Summary)
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ComparisonExportModal;